import { useState, useEffect, useRef, useCallback } from 'react';

const CACHE_NAME = 'magazine-videos-v1';
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24시간
const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * 여러 비디오 URL들에 대한 캐싱을 관리하는 커스텀 훅
 * @param {string[]} videoUrls - 캐싱할 비디오 URL 배열
 * @param {object} options - 캐싱 옵션
 * @param {boolean} options.preload - 즉시 캐싱할지 여부
 * @param {number} options.basePriority - 기본 우선순위 (첫 번째가 가장 높음)
 * @returns {object[]} 각 비디오별 캐시 상태와 제어 함수들의 배열
 */
export const useMultipleVideoCache = (videoUrls, options = {}) => {
  const { preload = false, basePriority = 7 } = options;
  
  const [cacheStates, setCacheStates] = useState([]);
  const abortControllersRef = useRef([]);
  const cachedUrlsRef = useRef([]);

  // 초기화
  useEffect(() => {
    const initialStates = videoUrls.map(() => ({
      isLoading: false,
      isCached: false,
      error: null,
      progress: 0
    }));
    setCacheStates(initialStates);
    
    // 기존 리소스 정리
    abortControllersRef.current.forEach(controller => {
      if (controller) controller.abort();
    });
    cachedUrlsRef.current.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    
    abortControllersRef.current = new Array(videoUrls.length).fill(null);
    cachedUrlsRef.current = new Array(videoUrls.length).fill(null);
  }, [videoUrls.length]);

  // 캐시에서 비디오 URL 확인
  const checkCache = useCallback(async (url) => {
    if (!url) return false;
    
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);
      
      if (response) {
        const cacheTime = response.headers.get('x-cache-time');
        if (cacheTime) {
          const age = Date.now() - parseInt(cacheTime);
          if (age > CACHE_EXPIRY_TIME) {
            await cache.delete(url);
            return false;
          }
        }
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Cache check failed:', error);
      return false;
    }
  }, []);

  // 캐시 크기 관리
  const manageCacheSize = useCallback(async () => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const requests = await cache.keys();
      let totalSize = 0;
      const cacheEntries = [];

      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const size = parseInt(response.headers.get('content-length') || '0');
          const cacheTime = parseInt(response.headers.get('x-cache-time') || '0');
          
          cacheEntries.push({
            request,
            size,
            cacheTime,
            priority: parseInt(response.headers.get('x-cache-priority') || '5')
          });
          totalSize += size;
        }
      }

      if (totalSize > MAX_CACHE_SIZE) {
        cacheEntries.sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return a.cacheTime - b.cacheTime;
        });

        for (const entry of cacheEntries) {
          if (totalSize <= MAX_CACHE_SIZE * 0.8) break;
          
          await cache.delete(entry.request);
          totalSize -= entry.size;
        }
      }
    } catch (error) {
      console.warn('Cache management failed:', error);
    }
  }, []);

  // 특정 인덱스의 비디오 캐싱
  const cacheVideo = useCallback(async (index) => {
    const url = videoUrls[index];
    if (!url || !cacheStates[index] || cacheStates[index].isLoading) return;

    setCacheStates(prev => {
      const next = [...prev];
      next[index] = { ...next[index], isLoading: true, error: null, progress: 0 };
      return next;
    });

    try {
      // 기존 요청 취소
      if (abortControllersRef.current[index]) {
        abortControllersRef.current[index].abort();
      }
      abortControllersRef.current[index] = new AbortController();

      // 이미 캐시되어 있는지 확인
      const isCached = await checkCache(url);
      if (isCached) {
        setCacheStates(prev => {
          const next = [...prev];
          next[index] = {
            isLoading: false,
            isCached: true,
            error: null,
            progress: 100
          };
          return next;
        });
        return;
      }

      const priority = basePriority - index;
      const response = await fetch(url, {
        signal: abortControllersRef.current[index].signal,
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength) : 0;
      let loaded = 0;

      const reader = response.body.getReader();
      const stream = new ReadableStream({
        start(controller) {
          function pump() {
            return reader.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }

              loaded += value.byteLength;
              if (total > 0) {
                const progress = Math.round((loaded / total) * 100);
                setCacheStates(prev => {
                  const next = [...prev];
                  next[index] = { ...next[index], progress };
                  return next;
                });
              }

              controller.enqueue(value);
              return pump();
            });
          }
          return pump();
        }
      });

      const clonedResponse = new Response(stream, {
        headers: {
          ...Object.fromEntries(response.headers),
          'x-cache-time': Date.now().toString(),
          'x-cache-priority': priority.toString()
        }
      });

      const cache = await caches.open(CACHE_NAME);
      await cache.put(url, clonedResponse);
      
      await manageCacheSize();

      setCacheStates(prev => {
        const next = [...prev];
        next[index] = {
          isLoading: false,
          isCached: true,
          error: null,
          progress: 100
        };
        return next;
      });

    } catch (error) {
      if (error.name === 'AbortError') {
        setCacheStates(prev => {
          const next = [...prev];
          next[index] = { ...next[index], isLoading: false };
          return next;
        });
        return;
      }

      console.error(`Video caching failed for index ${index}:`, error);
      setCacheStates(prev => {
        const next = [...prev];
        next[index] = {
          isLoading: false,
          isCached: false,
          error: error.message,
          progress: 0
        };
        return next;
      });
    }
  }, [videoUrls, cacheStates, basePriority, checkCache, manageCacheSize]);

  // 캐시된 비디오 URL 가져오기
  const getCachedVideoUrl = useCallback(async (index) => {
    const url = videoUrls[index];
    if (!url) return null;

    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);
      
      if (response) {
        const blob = await response.blob();
        
        // 이전 Blob URL 해제
        if (cachedUrlsRef.current[index]) {
          URL.revokeObjectURL(cachedUrlsRef.current[index]);
        }
        
        const cachedUrl = URL.createObjectURL(blob);
        cachedUrlsRef.current[index] = cachedUrl;
        return cachedUrl;
      }
      
      return url;
    } catch (error) {
      console.warn(`Failed to get cached video URL for index ${index}:`, error);
      return url;
    }
  }, [videoUrls]);

  // 초기 캐시 상태 확인
  useEffect(() => {
    const checkInitialCacheStates = async () => {
      const results = await Promise.all(
        videoUrls.map(url => checkCache(url))
      );
      
      setCacheStates(prev => 
        prev.map((state, index) => ({
          ...state,
          isCached: results[index]
        }))
      );
    };

    if (videoUrls.length > 0) {
      checkInitialCacheStates();
    }
  }, [videoUrls, checkCache]);

  // preload 옵션이 true인 경우 자동 캐싱
  useEffect(() => {
    if (preload && videoUrls.length > 0 && cacheStates.length > 0) {
      videoUrls.forEach((url, index) => {
        const state = cacheStates[index];
        if (url && state && !state.isCached && !state.isLoading) {
          cacheVideo(index);
        }
      });
    }
  }, [preload, videoUrls, cacheStates, cacheVideo]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => {
        if (controller) controller.abort();
      });
      cachedUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  // 각 비디오별 제어 객체 반환
  return cacheStates.map((state, index) => ({
    ...state,
    cacheVideo: () => cacheVideo(index),
    getCachedVideoUrl: () => getCachedVideoUrl(index),
    clearCache: async () => {
      try {
        const cache = await caches.open(CACHE_NAME);
        await cache.delete(videoUrls[index]);
        setCacheStates(prev => {
          const next = [...prev];
          next[index] = { ...next[index], isCached: false };
          return next;
        });
      } catch (error) {
        console.warn(`Failed to clear cache for index ${index}:`, error);
      }
    }
  }));
};